import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Table } from "@/components/ui/Table";
import { CreateChargeRuleModal } from "@/pages/stammdaten/CreateChargeRuleModal";
import type { ChargeRuleCreateInitialValues } from "@/pages/stammdaten/CreateChargeRuleModal";
import { EditChargeRuleModal } from "@/pages/stammdaten/EditChargeRuleModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { CHARGE_RULE_LABELS } from "@/lib/chargeRuleLabels";
import {
  CHARGE_RULE_GAP_STATUS_LABELS,
  chargeRulePriorityBadgeLabel,
  chargeRuleSlotKey,
  isOpenChargeRuleGap,
  type ChargeRuleCoverage,
  type ChargeRuleCoverageSlot,
} from "@/lib/chargeRuleCoverage";
import type { Article, ChargeRule, GuestTypeValue, PitchCategory } from "@/types/camping";

const RULE_LABELS = CHARGE_RULE_LABELS;

type ViewMode = "all" | "open";

function gapStatusClass(status: ChargeRuleCoverageSlot["status"]): string {
  switch (status) {
    case "missing":
      return "bg-status-orange/15 text-status-orange";
    case "inactive":
      return "bg-status-amber/15 text-status-amber";
    case "no_price":
      return "bg-status-red/15 text-status-red";
    default:
      return "bg-status-green/15 text-status-green";
  }
}

function priorityBadgeClass(priority: ChargeRuleCoverageSlot["priority"]): string {
  return priority === "required"
    ? "bg-status-red/10 text-status-red"
    : "bg-fill-tertiary text-label-secondary";
}

interface CoverageSlotRowProps {
  slot: ChargeRuleCoverageSlot;
  canWrite: boolean;
  rules: ChargeRule[];
  highlightOpen?: boolean;
  onEditRule: (rule: ChargeRule) => void;
  onCreateRule: (initialValues: ChargeRuleCreateInitialValues) => void;
}

function CoverageSlotRow({
  slot,
  canWrite,
  rules,
  highlightOpen = false,
  onEditRule,
  onCreateRule,
}: CoverageSlotRowProps) {
  const isOpen = isOpenChargeRuleGap(slot.status);

  return (
    <tr
      className={clsx(
        "border-b border-separator/60",
        highlightOpen && isOpen && "bg-status-orange/5",
      )}
    >
      <td>{RULE_LABELS[slot.rule_type] ?? slot.rule_type}</td>
      <td>{slot.pitch_category_name ?? "—"}</td>
      <td>
        <span
          className={clsx(
            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
            priorityBadgeClass(slot.priority),
          )}
        >
          {chargeRulePriorityBadgeLabel(slot.priority)}
        </span>
      </td>
      <td>
        <span
          className={clsx(
            "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
            gapStatusClass(slot.status),
          )}
        >
          {CHARGE_RULE_GAP_STATUS_LABELS[slot.status]}
        </span>
      </td>
      <td className="text-sm text-label-secondary">
        <div>{slot.status_detail ?? (isOpen ? "Zuordnung fehlt" : "Vollständig zugeordnet")}</div>
        {slot.note ? <div className="mt-1 text-label-tertiary">{slot.note}</div> : null}
        {slot.article_number ? (
          <div className="mt-1">
            Artikel: {slot.article_number}
            {slot.article_name ? ` – ${slot.article_name}` : ""}
          </div>
        ) : null}
      </td>
      {canWrite ? (
        <td className="text-right">
          {slot.rule_id ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                const rule = rules.find((item) => item.id === slot.rule_id);
                if (rule) onEditRule(rule);
              }}
            >
              Bearbeiten
            </Button>
          ) : isOpen ? (
            <Button
              type="button"
              size="sm"
              onClick={() =>
                onCreateRule({
                  rule_type: slot.rule_type,
                  pitch_category_id: slot.pitch_category_id,
                  guest_type: (slot.guest_type as GuestTypeValue | null) ?? null,
                })
              }
            >
              Zuordnung anlegen
            </Button>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

export function ChargeRulesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [rules, setRules] = useState<ChargeRule[]>([]);
  const [coverage, setCoverage] = useState<ChargeRuleCoverage | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ChargeRule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialValues, setCreateInitialValues] = useState<ChargeRuleCreateInitialValues | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<ViewMode>("open");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<ChargeRule[]>("/api/v1/charge-rules"),
      api.get<ChargeRuleCoverage>("/api/v1/charge-rules/coverage"),
      api.get<Article[]>("/api/v1/articles"),
      api.get<PitchCategory[]>("/api/v1/pitch-categories"),
    ])
      .then(([ruleList, coverageData, articleList, categoryList]) => {
        setRules(ruleList);
        setCoverage(coverageData);
        setArticles(articleList);
        setCategories(categoryList);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Buchungszuordnung" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  const allSlots = useMemo(() => coverage?.slots ?? [], [coverage]);

  const openSlots = useMemo(
    () => allSlots.filter((slot) => isOpenChargeRuleGap(slot.status)),
    [allSlots],
  );

  const displayedSlots = viewMode === "open" ? openSlots : allSlots;

  const requiredOpenCount = useMemo(
    () => openSlots.filter((slot) => slot.priority === "required").length,
    [openSlots],
  );

  function openCreateModal(initialValues?: ChargeRuleCreateInitialValues) {
    setCreateInitialValues(initialValues ?? null);
    setCreateOpen(true);
  }

  function closeCreateModal() {
    setCreateOpen(false);
    setCreateInitialValues(null);
  }

  return (
    <div className="space-y-6">
      <EditChargeRuleModal
        open={!!selected}
        rule={selected}
        articles={articles}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateChargeRuleModal
        open={createOpen}
        onClose={closeCreateModal}
        onCreated={load}
        articles={articles}
        categories={categories}
        initialValues={createInitialValues}
      />

      <Card title="Hinweis" bodyClassName="text-sm text-label-secondary">
        <p>
          Hier legen Sie fest, welcher Artikel bei einer Buchung für welche Leistung verwendet wird.
          Beispiel: Kategorie „Komfort“ → Artikel „Übernachtung Komfortstellplatz“. Für
          Besucheranmeldungen von Dauercampern stehen zusätzlich die Regeln Besucher Erwachsene,
          Besucher Kinder und Zusätzliche Autos zur Verfügung.
        </p>
      </Card>

      {!canWrite ? <StammdatenWriteHint /> : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {!loading && coverage ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2 text-sm">
              {coverage.open_count > 0 ? (
                <span className="rounded-full bg-status-orange/15 px-3 py-1 font-medium text-status-orange">
                  {coverage.open_count} offen
                  {requiredOpenCount > 0 ? ` (${requiredOpenCount} Pflicht)` : ""}
                </span>
              ) : (
                <span className="rounded-full bg-status-green/15 px-3 py-1 font-medium text-status-green">
                  Alle Zuordnungen vollständig
                </span>
              )}
              <span className="rounded-full bg-fill-tertiary px-3 py-1 text-label-secondary">
                {coverage.complete_count} vollständig
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: "open", label: `Offen (${coverage.open_count})` },
                  { key: "all", label: `Alle Zuordnungen (${allSlots.length})` },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={clsx(
                    "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                    viewMode === tab.key
                      ? "bg-accent text-label-on-accent"
                      : "bg-fill-tertiary text-label-secondary hover:text-label",
                  )}
                  onClick={() => setViewMode(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {canWrite ? (
            <div className="flex justify-end">
              <Button type="button" className="w-full sm:w-auto" onClick={() => openCreateModal()}>
                Neue Zuordnung
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table
          wide
          emptyMessage={
            viewMode === "open"
              ? "Alle erwarteten Zuordnungen sind vollständig."
              : "Keine Zuordnungen vorhanden."
          }
          isEmpty={displayedSlots.length === 0}
        >
          <thead>
            <tr>
              <th>Regel</th>
              <th>Kategorie</th>
              <th>Priorität</th>
              <th>Status</th>
              <th>Details</th>
              {canWrite ? <th className="text-right">Aktion</th> : null}
            </tr>
          </thead>
          <tbody>
            {displayedSlots.map((slot) => (
              <CoverageSlotRow
                key={chargeRuleSlotKey(slot)}
                slot={slot}
                canWrite={canWrite}
                rules={rules}
                highlightOpen={viewMode === "all"}
                onEditRule={setSelected}
                onCreateRule={openCreateModal}
              />
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
